import { create } from "zustand";

import { TokenStorage } from "./token-storage";
import { PasswordFormValues } from "../schema/user";
import { apiRoutes } from "@/config/api-route";
import { api } from "@/lib/auth";
import {
  LoginDto,
  CreateOrgDto,
  JoinOrgDto,
  BaseRegisterDto,
  VerifyBusinessDto,
  ForgotPasswordDto,
  ResetPasswordDto,
  TeamLoginDto,
} from "@/lib/schema/auth";
import {
  UserProfile,
  TokenPair,
  AuthResponse,
  VerificationResponse,
} from "@/types/auth";

interface AuthState {
  isAuthenticated: boolean;

  // Actions
  initialize: () => Promise<void>;
  login: (data: LoginDto) => Promise<AuthResponse>;
  loginVerifyOtp: (otp: string, token: string) => Promise<AuthResponse>;
  loginWithOtp: (data: LoginDto) => Promise<VerificationResponse>;
  teamLogin: (data: TeamLoginDto) => Promise<AuthResponse>;
  teamLoginWithOtp: (data: TeamLoginDto) => Promise<VerificationResponse>;
  forgotPassword: (data: ForgotPasswordDto) => Promise<VerificationResponse>;
  resetPassword: (data: ResetPasswordDto) => Promise<VerificationResponse>;
  logout: () => Promise<void>;
  register: (userData: BaseRegisterDto) => Promise<VerificationResponse>;
  createOrg: (userData: CreateOrgDto) => Promise<VerificationResponse>;
  joinOrg: (userData: JoinOrgDto) => Promise<AuthResponse>;
  verifyBusiness: (userData: VerifyBusinessDto) => Promise<AuthResponse>;
  refreshTokens: () => Promise<TokenPair>;
  updateProfile: (profileData: Partial<UserProfile>) => Promise<UserProfile>;
  updateProfileImage: (avatarUrl: string) => Promise<UserProfile>;
  updatePassword: (passwordData: PasswordFormValues) => Promise<UserProfile>;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,

  initialize: async () => {
    const token = await TokenStorage.getAccessToken();
    set({ isAuthenticated: !!token });
  },

  login: async (data: LoginDto) => {
    try {
      const response = await api.post<AuthResponse>(apiRoutes.auth.login, data);
      // Store tokens
      await TokenStorage.setTokens(response.tokens);

      // Store orgName
      if (response.user) {
        await TokenStorage.setAuthData(response.user);
      }

      set({ isAuthenticated: true });
      return response;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },

  loginWithOtp: async (data: LoginDto) => {
    try {
      const response = await api.post<VerificationResponse>(
        apiRoutes.auth.passwordLessLogin,
        data
      );
      return response;
    } catch (error) {
      console.error("Login with OTP error:", error);
      throw error;
    }
  },

  teamLogin: async (data: TeamLoginDto) => {
    try {
      const response = await api.post<AuthResponse>(
        apiRoutes.auth.teamLogin,
        data
      );
      // Store tokens
      await TokenStorage.setTokens(response.tokens);

      // Store orgName
      if (response.user) {
        await TokenStorage.setAuthData(response.user);
      }

      set({ isAuthenticated: true });
      return response;
    } catch (error) {
      console.error("Team login error:", error);
      throw error;
    }
  },

  teamLoginWithOtp: async (data: TeamLoginDto) => {
    try {
      const response = await api.post<VerificationResponse>(
        apiRoutes.auth.teamPasswordLessLogin,
        data
      );
      return response;
    } catch (error) {
      console.error("Team login with OTP error:", error);
      throw error;
    }
  },

  loginVerifyOtp: async (otp: string, token: string) => {
    try {
      const response = await api.post<AuthResponse>(apiRoutes.auth.verifyOtp, {
        otp,
        token,
      });

      await TokenStorage.setTokens(response.tokens);
      if (response.user) {
        await TokenStorage.setAuthData(response.user);
      }
      return response;
    } catch (error) {
      console.error("Verify OTP error:", error);
      throw error;
    }
  },

  forgotPassword: async (data: ForgotPasswordDto) => {
    try {
      const response = await api.post<VerificationResponse>(
        apiRoutes.auth.forgotPassword,
        data
      );

      return response;
    } catch (error) {
      console.error("Forgot password error:", error);
      throw error;
    }
  },

  resetPassword: async (data: ResetPasswordDto) => {
    try {
      const response = await api.post<VerificationResponse>(
        apiRoutes.auth.resetPassword,
        data
      );

      return response;
    } catch (error) {
      console.error("Reset password error:", error);
      throw error;
    }
  },

  logout: async () => {
    try {
      const refreshToken = await TokenStorage.getRefreshToken();
      if (refreshToken) {
        await api.post(apiRoutes.auth.logout, { refreshToken });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear tokens and update state
      TokenStorage.clearTokens();
      set({ isAuthenticated: false });
    }
  },

  register: async (userData: BaseRegisterDto) => {
    try {
      const response = await api.post<VerificationResponse>(
        apiRoutes.auth.register,
        userData
      );

      localStorage.setItem("verificationToken", response.token);

      return response;
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  },

  createOrg: async (userData: CreateOrgDto) => {
    try {
      const response = await api.post<VerificationResponse>(
        apiRoutes.auth.registerOrg,
        userData
      );

      localStorage.setItem("verificationToken", response.token);

      return response;
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  },

  joinOrg: async (userData: JoinOrgDto) => {
    try {
      const response = await api.post<AuthResponse>(
        apiRoutes.auth.joinOrg,
        userData
      );

      // Store tokens
      await TokenStorage.setTokens(response.tokens);

      // Store orgName
      if (response.user) {
        await TokenStorage.setAuthData(response.user);
      }

      set({ isAuthenticated: true });

      return response;
    } catch (error) {
      console.error("Join org error:", error);
      throw error;
    }
  },

  refreshTokens: async () => {
    const refreshToken = await TokenStorage.getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await api.post<{ tokens: TokenPair }>(
        apiRoutes.auth.refreshAccessToken,
        {
          refreshToken,
        }
      );

      // Store tokens
      await TokenStorage.setTokens(response.tokens);
      return response.tokens;
    } catch (error) {
      // Clear tokens and update state
      TokenStorage.clearTokens();
      set({ isAuthenticated: false });
      throw error;
    }
  },

  updateProfile: async (profileData: Partial<UserProfile>) => {
    try {
      const updatedUser = await api.patch<UserProfile>(
        apiRoutes.user.update,
        profileData
      );
      return updatedUser;
    } catch (error) {
      console.error("Profile update error:", error);
      throw error;
    }
  },

  updateProfileImage: async (avatarUrl: string) => {
    try {
      const updatedUser = await api.patch<UserProfile>(apiRoutes.user.update, {
        avatar: avatarUrl,
      });
      return updatedUser;
    } catch (error) {
      console.error("Profile update error:", error);
      throw error;
    }
  },

  updatePassword: async (passwordData: PasswordFormValues) => {
    try {
      const updatedUser = await api.patch<UserProfile>(
        apiRoutes.auth.changePassword,
        passwordData
      );
      return updatedUser;
    } catch (error) {
      console.error("Profile update error:", error);
      throw error;
    }
  },

  verifyBusiness: async (userData: VerifyBusinessDto) => {
    try {
      const response = await api.post<AuthResponse>(
        apiRoutes.auth.verifyBusiness,
        userData
      );
      // Store tokens
      await TokenStorage.setTokens(response.tokens);

      // Store orgName
      if (response.user) {
        await TokenStorage.setAuthData(response.user);
      }

      set({ isAuthenticated: true });
      return response;
    } catch (error) {
      console.error("Verify business error:", error);
      throw error;
    }
  },

  // Helper actions to update state
  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
}));

// Initialize auth on app start
export const initializeAuth = async () => {
  await useAuthStore.getState().initialize();
};
